/**
 * Fachliche Typen des Prüferbogens.
 *
 * Die Begriffe folgen Relay (siehe Relay `memory/osce-terminology.md`):
 * Station = `ExamItemContainer` mit `type = STATION`, Aufgabe = `ExamItem` mit `type = TASK`.
 * Hier stehen bewusst nur die Felder, die der Bogen anzeigt oder verändert — das vollständige
 * Servermodell kommt erst mit dem Relay-Adapter dazu.
 */

export type TaskId = string;

/** Eine Antwortstufe eines bewerteten Kriteriums. */
export interface CriterionOption {
  label: string;
  points: number;
}

export interface Criterion {
  label: string;
  options: CriterionOption[];
}

export interface ChecklistItem {
  text: string;
  /** Punkte, die das Häkchen einbringt. */
  points: number;
}

/**
 * Wie eine Aufgabe zu ihrer Punktzahl kommt. Der Bogen zeigt je nach Art einen anderen Körper an,
 * die Punktelogik in `scoring.ts` unterscheidet nur diese drei Fälle.
 */
export type TaskScoring =
  /** Globale Bewertung: Punktzahl wird direkt gesetzt; der Text dient als Orientierung, seine
   *  Wortgruppen lassen sich markieren (in Relay `ExamLogType = SELECTED_WORDS`). */
  | { kind: 'global'; max: number; body: string }
  /** Checkliste: Summe der angehakten Zeilen. */
  | { kind: 'checklist'; items: ChecklistItem[] }
  /** Bewertete Kriterien: Summe der je Kriterium gewählten Stufe. */
  | { kind: 'criteria'; criteria: Criterion[] };

export interface Task {
  id: TaskId;
  /** Überschrift der Aufgabe im Bogen. */
  title: string;
  /** Kurzform für Kopfzeilen und Popups. */
  short: string;
  /** Art der Bewertung im Klartext, z. B. „Checkliste, summiert". */
  typeLabel: string;
  scoring: TaskScoring;
  /** Schrittweite beim Setzen von Hand; fehlt sie, gilt die Einstellung halbe/ganze Punkte. */
  pointsStep?: number;
}

export interface Station {
  id: string;
  label: string;
  /** Raum o. Ä., steht klein neben dem Namen. */
  meta: string;
  /** Stationszeit in Sekunden (Relay: `ExamItemContainer.durationSeconds`). */
  durationSeconds: number;
  vignette?: Vignette;
  /**
   * Anhänge der Station, wie sie der Server führt (Bilder, PDF). Sie erscheinen in der
   * Vignette (Entscheidung des Nutzers, 2026-09-13).
   *
   * ⚠️ **Nicht zu verwechseln mit dem, was der Prüfer aufnimmt.** Das hier ist Material der
   * Prüfung; Aufnahmen sind Dateien unter `logs/` mit einem `ExamLog`-Eintrag.
   */
  medien?: Medium[];
  tasks: Task[];
}

/** Ein Anhang der Station. Die Datei liegt unter `media/<Prüfung>/<Kennung>.<Endung>`. */
export interface Medium {
  id: string;
  name: string;
  /** Freitext des Servers, oft leer. */
  description: string;
  mimeType: string;
  /** Wahr, wenn die Anzeige es als Bild zeigen kann. */
  istBild: boolean;
}

export interface Vignette {
  teaser: string;
  lead: string;
  task: string;
  /**
   * Anleitung für die Prüferin — was sie vorbereiten, sagen oder beobachten soll.
   *
   * Optional, weil Relay' eigene Prüfungen sie (noch) nicht führen. Im Classic-Bestand gibt es
   * sie dagegen reichlich: dort steckt sie in einem `javascript:`-Link im Stationstext
   * (`server/classic/abbildung.ts::pruefInstruktionen`) — 29 Stationen von 86 tragen eine.
   */
  instructions?: string;
  /**
   * Der Situationstext mit seiner Auszeichnung, wie der Server ihn schickt. Ist er da, zeigt
   * die Vignette ihn statt `lead` (bereinigt, `ui/HtmlText`, seit 2026-10-02). Ältere
   * Ablagen führen ihn nicht.
   */
  html?: string;
}

export interface Person {
  id: string;
  name: string;
  meta: string;
}

/**
 * Zustand eines Prüflings. Die Schlüssel bleiben deutsch, weil sie fachliche Werte sind und
 * kein Anzeigetext — die Beschriftung steht im Sprachkatalog (`t.candidates.state`).
 */
export type CandidateState = 'wartet' | 'laeuft' | 'bewertet' | 'entschuldigt';

export interface Candidate {
  /** Relay-Kennung. Sie wandert in `ExamLog.candidateID`. */
  id: string;
  /** Vor- und Nachname zusammen, für die Anzeige in einer Zeile. */
  name: string;
  /**
   * Beide Teile einzeln — die Liste lässt sich nach Nachname **oder** Vorname sortieren, und
   * aus `name` liesse sich das nicht zurückgewinnen: „Anna Maria Berg" hat keine erkennbare
   * Fuge (Vorgabe des Nutzers, 2026-09-13).
   */
  firstName: string;
  lastName: string;
  /** Was der Prüfling gedruckt bei sich trägt; der QR-Scanner liest diese Nummer. */
  matriculationNumber: string;
  /**
   * Die laufende Nummer **in dieser Prüfung** (`ExamCandidate.examCandidateNo`, im
   * Classic-Bestand `osceExamineeNo`). Optional: nicht jede Prüfung vergibt eine.
   *
   * ⚠️ Nicht zu verwechseln mit der Matrikelnummer — die gehört zur Person und gilt über alle
   * Prüfungen hinweg.
   */
  examNo: string | null;
  state: CandidateState;
  /** Bereits abgegebene Gesamtpunktzahl, sonst `null`. */
  score: number | null;
}

export interface Exam {
  id: string;
  name: string;
  meta: string;
  /** Ob die Prüfungsdaten auf dem Gerät liegen. Nicht geladene lassen sich nur nachladen. */
  loaded: boolean;
  /**
   * Simulationsprüfung: Schulung, Vorführung, Demo. Nichts davon wird aufgezeichnet — **die
   * App sendet nicht**, denn der Server filtert nicht (s. `persistence/schema.ts::AUFSTIEG_5`).
   *
   * ⚠️ **Der Prüfer muss es sehen, durchgehend.** Wer eine Station bewertet und nicht weiss,
   * dass die Eingaben verworfen werden, bewertet ins Leere und merkt es erst, wenn die
   * Ergebnisse fehlen. Deshalb eine stehende Warnung im Bogen, nicht ein Hinweis, der einmal
   * aufblitzt (Vorgabe des Nutzers, 2026-09-18).
   */
  simulativ?: boolean;
  /** Kam als Preview über Message (`src/preview/`). Dann kein Bogenabzug — die Bilder wären Beweisstücke einer Probe. */
  vorschau?: boolean;
  stations: Station[];
  examiners: Person[];
  actors: Person[];
  candidates: Candidate[];
}

/** Ein Medienanhang eines Kommentars. */
export interface CommentMedia {
  id: string;
  /** Aufnahmezeit als `HH:MM`. */
  time: string;
  /** Dauer in Sekunden, nur bei Audio und Video. */
  durationSeconds?: number;
  /**
   * Dateiname im Ablageschema `logs/<Bezug>/<Art>`, sobald eine echte Datei dahintersteht.
   * Solange die nativen Plugins fehlen, bleibt das Feld leer — der Eintrag ist dann eine
   * Attrappe und die Oberfläche sagt das auch.
   */
  fileName?: string;
}

/** Die drei Arten von Medienanhängen. Zugleich der Ordnername in der Ablage. */
export type MediaKind = 'audio' | 'photo' | 'video';

/** Ein Punkt einer Handschriftlinie. Koordinaten liegen zwischen 0 und 1. */
export interface InkPoint {
  x: number;
  y: number;
  /** Stiftdruck, 0…1; ohne Apple Pencil bleibt es bei einem festen Wert. */
  p: number;
}

/**
 * Ein Strich, vom Aufsetzen bis zum Abheben.
 *
 * Die Koordinaten sind bewusst auf die Fläche normiert und nicht in Pixeln: die Schreibfläche
 * ist Teil eines responsiven Layouts und ändert beim Drehen ihre Größe.
 */
export interface InkStroke {
  points: InkPoint[];
}

export interface CommentBox {
  text: string;
  audios: CommentMedia[];
  photos: CommentMedia[];
  videos: CommentMedia[];
  /** Die Handschriftseite; leer heißt: nichts geschrieben. */
  handwriting: InkStroke[];
}

export const emptyComment = (): CommentBox => ({
  text: '',
  audios: [],
  photos: [],
  videos: [],
  handwriting: [],
});

/** Knoten des Baustein-Baums im Kommentar-Reiter „Text & Bausteine". */
export interface SnippetNode {
  /** Ordner tragen ein Label und Kinder. */
  label?: string;
  /** Blätter tragen den einfügbaren Text. */
  text?: string;
  children?: SnippetNode[];
}
